import { AnimatePresence, motion } from 'framer-motion';
import { SPRING, TRANSITION } from 'shared/components/motion/tokens';
import { AREAS, areaDefinition } from 'modules/arena/reports/model/areas';
import {
  ClassesBody,
  CourtsBody,
  FinancialBody,
  InstructorsBody,
  OverviewBody,
  PackagesBody,
  TournamentsBody,
} from 'modules/arena/reports/model/bodies';
import { AreaResponse, ReportMeta } from 'modules/arena/reports/model/types';
import { ReportsController, useReportsController } from 'modules/arena/reports/hooks/useReportsController';
import { DemoBanner } from 'modules/arena/reports/components/DemoBanner';
import { AppliedChips, FilterNotices, FilterPanel } from 'modules/arena/reports/components/FilterPanel';
import { ExportControl } from 'modules/arena/reports/components/ExportControl';
import { ReportMetaBar } from 'modules/arena/reports/components/ReportMetaBar';
import { ReportLoading, RetryButton, StateView } from 'modules/arena/reports/components/StateView';
import { OverviewArea } from 'modules/arena/reports/areas/OverviewArea';
import { FinancialArea } from 'modules/arena/reports/areas/FinancialArea';
import { ClassesArea } from 'modules/arena/reports/areas/ClassesArea';
import { InstructorsArea } from 'modules/arena/reports/areas/InstructorsArea';
import { PackagesArea } from 'modules/arena/reports/areas/PackagesArea';
import { CourtsArea } from 'modules/arena/reports/areas/CourtsArea';
import { TournamentsArea } from 'modules/arena/reports/areas/TournamentsArea';
import { Button } from 'shared/components/ui/Button';
import { useArenaProfile } from 'shared/contexts/ArenaProfileContext';
import { cn } from 'shared/utils/cn';

function AreaContent({ response, meta, controller }: { response: AreaResponse; meta: ReportMeta; controller: ReportsController }) {
  const body = response.body as unknown;
  const props = { meta, controller };
  switch (controller.area) {
    case 'visao-geral':
      return <OverviewArea body={body as OverviewBody} {...props} />;
    case 'financeiro':
      return <FinancialArea body={body as FinancialBody} {...props} />;
    case 'aulas':
      return <ClassesArea body={body as ClassesBody} {...props} />;
    case 'professores':
      return <InstructorsArea body={body as InstructorsBody} {...props} />;
    case 'pacotes':
      return <PackagesArea body={body as PackagesBody} {...props} />;
    case 'quadras':
      return <CourtsArea body={body as CourtsBody} {...props} />;
    case 'torneios':
      return <TournamentsArea body={body as TournamentsBody} {...props} />;
  }
}

/** Resultado da consulta da área: cada estado tem sua tela, nunca números no lugar de erro. */
function AreaResult({ controller }: { controller: ReportsController }) {
  const { query, retry, clearFilters } = controller;

  // Enquanto carrega, nada da consulta anterior fica na tela (pode ser de outro filtro ou área).
  if (query.status === 'idle' || query.status === 'loading') return <ReportLoading />;
  if (query.status === 'error') {
    return <StateView variant="error" message="Verifique a conexão e tente de novo. Nenhum número foi exibido no lugar." actions={<RetryButton onClick={retry} />} />;
  }

  const { response } = query;
  switch (response.status) {
    case 'denied':
      return <StateView variant="denied" message={response.message ?? 'Seu perfil não tem acesso a este relatório.'} />;
    case 'not_connected':
      return <StateView variant="not_connected" message={response.message} />;
    case 'error':
      return <StateView variant="error" message={response.message} actions={<RetryButton onClick={retry} />} />;
    case 'empty':
      return (
        <div className="flex flex-col gap-4">
          {response.meta && <ReportMetaBar meta={response.meta} />}
          <StateView variant="empty" message={response.message ?? 'Não houve movimentação neste período.'} />
        </div>
      );
    case 'no_results':
      return (
        <div className="flex flex-col gap-4">
          {response.meta && <ReportMetaBar meta={response.meta} />}
          <StateView
            variant="no_results"
            message={response.message ?? 'Há movimentação no período, mas nada corresponde aos filtros aplicados.'}
            actions={
              <Button variant="neutral" onClick={clearFilters}>
                Limpar filtros
              </Button>
            }
          />
        </div>
      );
  }

  if (!response.meta || !response.body) return <StateView variant="error" message="Resposta incompleta da fonte de dados." actions={<RetryButton onClick={retry} />} />;
  return (
    <div className="flex flex-col gap-6">
      <ReportMetaBar meta={response.meta} />
      <AreaContent response={response} meta={response.meta} controller={controller} />
    </div>
  );
}

export function ArenaReportsPage() {
  const controller = useReportsController();
  const { profile } = useArenaProfile();
  const { source, scenario, setScenario, contextState, context, area, applied, notices, dismissNotices, selectArea, retry, query } = controller;
  // Sem acesso, erro ou fonte desconectada: nada para exportar (e não se gera arquivo com dados negados).
  const resultAllowsExport = query.status === 'done' && ['ok', 'empty', 'no_results'].includes(query.response.status);
  const definition = areaDefinition(area);
  const areas = context ? AREAS.filter((item) => context.permissions.areas.includes(item.id)) : [];

  return (
    <div className="flex min-w-0 flex-1 flex-col gap-5 p-4 sm:p-6">
      <header className="flex flex-col gap-1">
        <p className="text-xs font-semibold uppercase tracking-wider text-primary-dark">{context?.catalog.arenaName ?? profile.arenaName}</p>
        <h1 className="font-heading text-2xl font-bold text-ink">Relatórios</h1>
        <p className="text-sm text-ink-secondary">{definition.description}</p>
      </header>

      {source.capabilities.demo && <DemoBanner scenario={scenario} onScenarioChange={setScenario} />}

      {contextState.status === 'loading' && <ReportLoading />}
      {contextState.status === 'not_connected' && <StateView variant="not_connected" message={contextState.message} />}
      {contextState.status === 'denied' && <StateView variant="denied" message={contextState.message} />}
      {contextState.status === 'error' && <StateView variant="error" message={contextState.message} actions={<RetryButton onClick={retry} />} />}

      {context && (
        <>
          <nav aria-label="Áreas de relatório" className="-mx-4 flex gap-1 overflow-x-auto border-b border-border/60 px-4 sm:-mx-6 sm:px-6 print:hidden">
            {areas.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => item.id !== area && selectArea(item.id)}
                aria-current={item.id === area ? 'page' : undefined}
                className={cn(
                  'relative whitespace-nowrap px-3 py-2.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                  item.id === area ? 'text-primary-dark' : 'text-ink-secondary hover:text-ink'
                )}
              >
                {item.label}
                {item.id === area && (
                  <motion.span layoutId="report-area-underline" transition={SPRING} className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-primary" />
                )}
              </button>
            ))}
          </nav>

          <FilterPanel controller={controller} definition={definition} catalog={context.catalog} capabilities={source.capabilities} />
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <AppliedChips controller={controller} catalog={context.catalog} />
            <ExportControl source={source} area={area} filters={applied} canExport={context.permissions.canExport}
              blockedReason={resultAllowsExport ? undefined : query.status === 'loading' || query.status === 'idle' ? 'Exportação disponível após carregar o relatório.' : 'Exportação indisponível para este resultado.'} />
          </div>
          <FilterNotices notices={notices} onDismiss={dismissNotices} />

          <AnimatePresence mode="wait">
            <motion.div key={area} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={TRANSITION}>
              <AreaResult controller={controller} />
            </motion.div>
          </AnimatePresence>
        </>
      )}
    </div>
  );
}
